import React, {useEffect, useRef, useState} from 'react';
import {DataGrid, GridColDef, GridPaginationModel, GridRowParams, GridSortModel, GRID_CHECKBOX_SELECTION_COL_DEF} from '@mui/x-data-grid';
import {fetchOrderById, fetchOrderCsv, fetchOrdersPage, fetchOrderStatuses, markReadyForPickup, markComplete, markCancelled, markMobileDelivered} from '../../api/endpoints';
import ExpandedOrder from '../../interfaces/ExpandedOrder';
import {DateRangeMenu, SingleSelectMenu} from '../../utils/DropwdownUtils/DropdownUtils';
import {isRight} from 'fp-ts/lib/Either';
import Modal from 'react-modal';
import AdminOrderModal from './AdminOrderModal';
import {formatDateObjToMonthAndDay, formatDateToSlash} from '../../utils/DateUtils/DateUtils';


interface RowData {
    id: number;
    userFirstName: string,
    userLastName: string,
    eventName: string;
    eventVenue: string;
    eventDate: string;
    purchaseDate: string;
    section: string;
    row: string;
    quantity: number;
    price: number;
    orderStatus: boolean;
    lastFourDigits: string;
}

const AdminOrders: React.FC = () => {
    const datePickerRef = useRef<HTMLDivElement>(null);
    const statusRef = useRef<HTMLDivElement>(null);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [activeDatePicker, setActiveDatePicker] = useState(false);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [statusOptions, setStatusOptions] = useState<string[]>([]);
    const [filterQuery, setFilterQuery] = useState('');
    const [lastSearched, setLastSearched] = useState('');
    const [selectedOrder, setSelectedOrder] = useState<ExpandedOrder | null>(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [activeStatusPicker, setActiveStatusPicker] = useState(false);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [rows, setRows] = useState<RowData[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "id",
            sort: "desc"
        }
    ]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 10,
    });
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [loadingAction, setLoadingAction] = useState(false);

    const statusFiltered = selectedStatus !== '';

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 60, disableColumnMenu: true },
        { field: 'purchaseDate', headerName: 'Order Date', width: 103, disableColumnMenu: true, renderCell: (params) => (formatDateToSlash(params.value)) },
        { field: 'userFirstName', headerName: 'First Name', width: 120, disableColumnMenu: true },
        { field: 'userLastName', headerName: 'Last Name', width: 120, disableColumnMenu: true },
        { field: 'eventName', headerName: 'Event Name', width: 285, disableColumnMenu: true },
        { field: 'eventDate', headerName: 'Event Date', width: 103, disableColumnMenu: true, renderCell: (params) => (formatDateToSlash(params.value)) },
        { field: 'price', headerName: 'Price', width: 70, disableColumnMenu: true },
        { field: 'orderStatus', headerName: 'Order Status', width: 140, disableColumnMenu: true },
        { field: 'lastFourDigits', headerName: 'Last 4', width: 67, disableColumnMenu: true },
        { ...GRID_CHECKBOX_SELECTION_COL_DEF, width: 50, disableColumnMenu: true }
    ];

    const fetchData = async (search: string, paginationModel: GridPaginationModel, sortModel: GridSortModel) => {
        setLoadingOrders(true);
        const page = paginationModel.page;
        const pageSize = paginationModel.pageSize;
        const sortBy = sortModel[0]?.field || "id";
        const order = sortModel[0]?.sort || "desc";
        const ordersDataRepsonseEither = await fetchOrdersPage(page, pageSize, search, selectedStatus, sortBy, order);
        if (isRight(ordersDataRepsonseEither)) {
            const { content, total, pageable } = ordersDataRepsonseEither.right;
            setRows(content);
            setTotalRows(total);
        } else {
            console.error(ordersDataRepsonseEither.left)
        }

        setLoadingOrders(false);
    };

    const refetchData = async () => {
        fetchData(lastSearched, paginationModel, sortModel);
    }

    useEffect(() => {
        const fetchStatusOptions = async () => {
            const statusOptionsResponseEither = await fetchOrderStatuses();
            if (isRight(statusOptionsResponseEither)) {
                setStatusOptions(statusOptionsResponseEither.right);
            } else {
                console.error(statusOptionsResponseEither.left)
            }
        }

        const handleClickOutsideStatusPicker = (e: MouseEvent) => {
            if (statusRef.current && e.target instanceof Node && !statusRef.current.contains(e.target)) {
                setActiveStatusPicker(false);
            }
        };

        const handleClickOutsideDatePicker = (e: MouseEvent) => {
            if (datePickerRef.current && e.target instanceof Node && !datePickerRef.current.contains(e.target)) {
                setActiveDatePicker(false);
            }
        };


        fetchStatusOptions();
        document.addEventListener('mousedown', handleClickOutsideDatePicker);
        document.addEventListener('mousedown', handleClickOutsideStatusPicker);

        return () => {
            document.removeEventListener('mousedown', handleClickOutsideDatePicker);

            document.removeEventListener('mousedown', handleClickOutsideStatusPicker);
        };
    }, []);


    useEffect(() => {
        fetchData(lastSearched, paginationModel, sortModel);
    }, [paginationModel, sortModel]);

    useEffect(() => {
        fetchData(lastSearched, paginationModel, sortModel);
    }, [selectedStatus]);


    const handleRowClick = async (params: GridRowParams) => {
        const responseEither = await fetchOrderById(params.row.id)
        if (isRight(responseEither)) {
            setSelectedOrder(responseEither.right);
            setOpenDialog(true);
        } else {
            console.error(responseEither.left)
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedOrder(null);
    };

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilterQuery(e.target.value);
    };

    const handleFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        fetchData(filterQuery, paginationModel, sortModel);
        setLastSearched(filterQuery);
    }

    const handlePaginationModelChange = (model: GridPaginationModel) => {
        setPaginationModel(model);
    };

    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    };

    const submitExportCsv = async () => {
        if (dateRange[0] && dateRange[1]) {
            console.log(dateRange[0])
            console.log(dateRange[1])
            const responseEither = await fetchOrderCsv(dateRange[0], dateRange[1]);
            if (isRight(responseEither)) {
                try {
                    const csvString = responseEither.right;
                    const blob = new Blob([csvString], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'orders.csv';
                    link.click();

                    window.URL.revokeObjectURL(url);
                } catch (error) {
                    console.error("Download failed:', error");
                }
            } else {
                console.error(responseEither.left)
            }
        }
    }

    const dateFiltered = dateRange[1] !== null;
    const dateRangeString = (dateRange[0] && dateRange[1]) ? formatDateObjToMonthAndDay(dateRange[0]) + " - " + formatDateObjToMonthAndDay(dateRange[1]) : '';

    const handleStatusSelect = (status: string) => {
        setNewStatus(status);
        setShowConfirmationModal(true);
    };

    const handleConfirmStatusUpdate = async () => {
        if (selectedRows.length === 0 || !newStatus) return;
        
        setLoadingAction(true);
        try {
            const updatePromises = selectedRows.map(orderId => {
                switch (newStatus) {
                    case 'Ready for Pickup':
                        return markReadyForPickup({ id: orderId });
                    case 'Complete':
                        return markComplete({ id: orderId });
                    case 'Cancelled':
                        return markCancelled(orderId);
                    case 'Mobile Transferred':
                        return markMobileDelivered({ id: orderId, links: [] });
                    default:
                        return Promise.resolve();
                }
            });

            // wait for all updates
            const results = await Promise.all(updatePromises);
            
            const hasErrors = results.some((result: any) => !isRight(result));
            if (hasErrors) {
                console.error('Some updates failed');
            }

            setSelectedRows([]);
            setNewStatus('');
            setShowConfirmationModal(false);
            await refetchData();
        } catch (error) {
            console.error('Failed to update order statuses:', error);
        } finally {
            setLoadingAction(false);
        }
    };

    return (
        <div className='card card-top' style={{ minHeight: 500, width: '100%' }} >
            <div className='row'>
                <div className='left'>
                    <div className='text-l'>Orders</div>
                </div>
                <div className='relative' ref={datePickerRef}>
                    <button className={`admin-button ${dateFiltered && 'admin-filtered'}`}
                            onClick={(() => setActiveDatePicker(!activeDatePicker))}>{(dateFiltered) ? dateRangeString : "Select Date Range to Export"}</button>
                    {activeDatePicker &&
                        <DateRangeMenu
                            startDate={dateRange[0]}
                            endDate={dateRange[1]}
                            onChange={(dates) => setDateRange(dates)}
                            onClose={() => setActiveDatePicker(false)}
                        />
                    }
                </div>
                {(dateRange[0] && dateRange[1]) &&
                    <>
                        <div className='gap-s'></div>
                        <button type="submit" className='admin-delete-button' onClick={submitExportCsv}>Export CSV</button>
                    </>
                }
            </div>
            <div className='card-item'>
                <form onSubmit={handleFilterSubmit} className='card-item'>
                    <div className='row'>
                        <input type="text" value={filterQuery} onChange={handleQueryChange}
                               placeholder="Type to filter by id, first name, last name"
                               className='admin-search'
                        />
                        <button type="submit" className='admin-action-button'>Filter</button>
                        <div className='gap-s'/>
                        <div className='relative' ref={statusRef}>
                            <button className={`admin-button ${statusFiltered && 'admin-filtered'}`} 
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveStatusPicker(!activeStatusPicker);
                                }}
                            >
                                {statusFiltered ? selectedStatus : "Order Status"}
                            </button>
                            {activeStatusPicker &&
                                <SingleSelectMenu
                                    options={statusOptions}
                                    selectedOption={selectedStatus}
                                    setSelectedOption={setSelectedStatus}
                                    closeMenu={() => setActiveStatusPicker(false)}
                                    mustSelect={false}
                                    rightAligned={true}
                                />
                            }
                        </div>
                        <div className='gap-s'/>
                        <button className='admin-button' style={{ padding: '4px 12px' }}
                            onClick={(e) => {
                                e.preventDefault();
                                setFilterQuery('');
                                setSelectedStatus('');
                                setLastSearched('');
                                setSelectedRows([]);
                                fetchData('', paginationModel, sortModel);
                            }}
                        >
                            ↻
                        </button>
                        {selectedRows.length > 0 && (
                            <>
                                <div className='gap-s'/>
                                <button className='admin-action-button' style={{ fontSize: '0.7em', padding: '8px 12px' }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleStatusSelect('Ready for Pickup');
                                    }}
                                >
                                    {selectedRows.length} &#x27A1; Ready for Pickup
                                </button>
                                <div className='gap-s'/>
                                <button className='admin-action-button' style={{ fontSize: '0.7em', padding: '8px 12px' }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleStatusSelect('Complete');
                                    }}
                                >
                                    {selectedRows.length} &#x27A1; Complete
                                </button>
                                <div className='gap-s'/>
                                <button className='admin-delete-button' style={{ fontSize: '0.7em', padding: '8px 12px' }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleStatusSelect('Cancelled');
                                    }}
                                >
                                    {selectedRows.length} &#x27A1; Cancelled
                                </button>
                                <div className='gap-s'/>
                                <button className='admin-action-button' style={{ fontSize: '0.7em', padding: '8px 12px' }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleStatusSelect('Mobile Transferred');
                                    }}
                                >
                                    {selectedRows.length} &#x27A1; Mobile Transferred
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </div>
            {loadingOrders ?
                <div className="loading-spinner"></div>
                :
                <DataGrid
                    sx={{ '& .MuiCheckbox-root svg': {fontSize: '19px' } }} 
                    rows={rows}
                    columns={columns}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    rowCount={totalRows}
                    pageSizeOptions={[5, 10, 20]}
                    onRowClick={handleRowClick}
                    className='admin-data-grid'
                    checkboxSelection disableRowSelectionOnClick
                    onRowSelectionModelChange={(newSelection) => {
                        setSelectedRows(newSelection as number[]);
                    }}
                    rowSelectionModel={selectedRows}
                />
            }

            <Modal 
                isOpen={showConfirmationModal} 
                onRequestClose={() => {
                    if (!loadingAction) {
                        setShowConfirmationModal(false);
                        setNewStatus('');
                    }
                }}
                style={{
                    content: {
                        display: 'flex',
                        flexDirection: 'column',
                        maxWidth: '600px',
                        width: 'auto',
                        height: 'auto',
                        maxHeight: '80vh',
                        margin: 'auto',
                        top: '100px',
                        bottom: 'auto',
                        padding: '30px',
                        borderRadius: '15px'
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    }
                }}
                appElement={document.getElementById('root') || undefined}
            >
                {loadingAction ? (
                    <div className="loading-spinner" style={{ margin: '20px auto' }}></div>
                ) : (
                    <>
                        <div>
                            <h2 style={{ marginTop: 0 }}>Confirm Status Update</h2>
                            <p>Are you sure you want to change the status of the following {selectedRows.length} orders to "<strong>{newStatus}</strong>"?</p>
                            <div>
                                {rows
                                    .filter(row => selectedRows.includes(row.id))
                                    .map(row => (
                                        <div key={row.id} style={{ padding: '5px 0', borderBottom: '1px solid #eee' }}>
                                            (Current: {row.orderStatus}) <strong>{row.userLastName}</strong> - {row.eventName} 
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <button className='admin-button'
                                onClick={() => {
                                    setShowConfirmationModal(false);
                                    setNewStatus('');
                                }}
                            >
                                Cancel
                            </button>
                            <button className='admin-action-button' onClick={handleConfirmStatusUpdate}>
                                Yes, Update to '{newStatus}'
                            </button>
                        </div>
                    </>
                )}
            </Modal>

            <Modal isOpen={openDialog} onRequestClose={handleCloseDialog} style={{
                content: {
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '1000px',
                    margin: 'auto',
                    top: '125px',
                    bottom: '40px',
                    padding: '40px',
                    borderRadius: '15px'
                },
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                }
            }}
                appElement={document.getElementById('root') || undefined}
            >
                <>
                    {selectedOrder && (
                        <AdminOrderModal order={selectedOrder} refetchData={refetchData} />
                    )}
                </>
            </Modal>
        </div>
    )
}

export default AdminOrders;
